// Sign-off circles per rung of the workpaper's scheme. Filled with initials when signed (hover: who and the engine's stamp);
// an invalidated sign-off is struck through with its reason. Circles are filled only by what the engine published.
import type { RoleSignoffState, Signoff, SignoffRole } from "../data/types";
import { fmtStamp, initials } from "../lib/format";

const RUNG: Record<SignoffRole, string> = { preparer: "P", reviewer: "R", partner: "EP", eqr: "EQ" };
const FILL: Record<SignoffRole, string> = { preparer: "bg-progress text-paper border-progress", reviewer: "bg-done text-paper border-done", partner: "bg-brand text-brand-ink border-brand", eqr: "bg-agent text-paper border-agent" };

/** `roles`: the engine's sign-off rules per role (workpapers.state.roles); when published, a circle is filled from its `signed`. */
export function SignoffCircles({ signoffs, scheme = ["preparer", "reviewer"], roles }: { signoffs: Signoff[]; scheme?: SignoffRole[]; roles?: Partial<Record<SignoffRole, RoleSignoffState>> | null }) {
  return (
    <span className="inline-flex gap-1" role="list" aria-label="Sign-offs">
      {scheme.map((r) => {
        const signed = roles?.[r]?.signed;
        const fromRules = signed?.length ? { person: signed[signed.length - 1].person, at: signed[signed.length - 1].at, agent: null } : undefined;
        const current = roles ? fromRules : signoffs.find((x) => x.role === r && !x.invalidatedAt);
        const invalid = !current ? signoffs.find((x) => x.role === r && x.invalidatedAt) : undefined;
        const label = current ? `${r}: ${current.person}, ${fmtStamp(current.at)}${current.agent ? ` (agent ${current.agent})` : ""}`
          : invalid ? `${r}: ${invalid.person}'s sign-off invalidated — ${invalid.invalidatedReason ?? "changed since"}` : `${r}: not signed`;
        return (
          <span key={r} role="listitem" aria-label={label} title={label}
            className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full border px-1 text-[11px] font-semibold ${current ? FILL[r] : invalid ? "border-stale-line text-stale line-through" : "border-line text-muted"}`}>
            {current ? initials(current.person) : invalid ? initials(invalid.person) : RUNG[r]}
          </span>
        );
      })}
    </span>
  );
}
